import { supabase } from '@/lib/supabase'

// ── Admin user management ──────────────────────────────────────────

export async function fetchAllUsers({
  limit = 50,
  offset = 0,
  search = null,
  role = null,
  status = null,
  sortColumn = 'created_at',
  sortDirection = 'desc',
} = {}) {
  const { data, error } = await supabase.rpc('get_all_users', {
    p_limit: limit,
    p_offset: offset,
    p_search: search,
    p_role: role,
    p_status: status,
    p_sort_column: sortColumn,
    p_sort_direction: sortDirection,
  })
  if (error) throw error
  return data || { users: [], total_count: 0 }
}

export async function updateUserProfileAdmin({ user_id_param, new_first_name, new_last_name, new_email }) {
  const { error } = await supabase.rpc('update_user_profile_admin', {
    user_id_param,
    new_first_name,
    new_last_name,
    new_email,
  })
  if (error) throw error
}

export async function updateUserRole({ user_id_param, new_role_param }) {
  const { error } = await supabase.rpc('update_user_role', {
    user_id_param,
    new_role_param,
  })
  if (error) throw error
}

export async function updateUserStatus({ user_id_param, new_status_param }) {
  const { error } = await supabase.rpc('update_user_status', {
    user_id_param,
    new_status_param,
  })
  if (error) throw error
}

export async function updateUserEmailOptOut({ user_id_param, new_opt_out_param }) {
  const { error } = await supabase.rpc('update_user_email_opt_out', {
    user_id_param,
    new_opt_out_param,
  })
  if (error) throw error
}

export async function deleteUser({ user_id_param }) {
  const { data, error } = await supabase.rpc('delete_user', {
    user_id_param,
  })
  if (error) throw error
  return data
}

// ── Profile (player-facing) ────────────────────────────────────────

export async function getProfileById(p_user_id) {
  const { data, error } = await supabase.rpc('get_profile_by_id', { p_user_id })
  if (error) throw error
  return data
}

export async function getEntries({ p_user_id, p_sport_id = null, p_tournament_id = null, p_created_from = null, p_created_to = null, p_limit = 50, p_offset = 0 }) {
  const { data, error } = await supabase.rpc('get_entries', {
    p_user_id,
    p_sport_id,
    p_tournament_id,
    p_created_from,
    p_created_to,
    p_limit,
    p_offset,
  })
  if (error) throw error
  return data
}

// ── Settings / self-service profile update ─────────────────────────

export async function updateUserProfile(userId, { first_name, last_name, city, state_code, country, phone }) {
  const payload = { first_name, last_name, city, state_code, country }
  if (phone !== undefined) payload.phone = phone
  const { error } = await supabase
    .from('user_profiles')
    .update(payload)
    .eq('id', userId)
  if (error) throw error
}

export async function updateMyBetaFeatures(enabled) {
  const { data, error } = await supabase.rpc('update_my_beta_features', {
    p_value: enabled,
  })
  if (error) throw error
  return data
}

// ── Avatar ─────────────────────────────────────────────────────────

export async function updateMyAvatar({ avatarType, avatarUrl }) {
  const { data, error } = await supabase.rpc('update_my_avatar', {
    p_avatar_type: avatarType,
    p_avatar_url: avatarUrl,
  })
  if (error) throw error
  return data
}

export async function uploadAvatarPhoto(userId, file) {
  const ext = (file.name?.split('.').pop() || 'png').toLowerCase()
  const path = `${userId}/avatar-${Date.now()}.${ext}`
  const { error } = await supabase.storage
    .from('avatars')
    .upload(path, file, { upsert: true, contentType: file.type || 'image/png' })
  if (error) throw error
  const { data } = supabase.storage.from('avatars').getPublicUrl(path)
  return data?.publicUrl || null
}

// ── Streaks & badges ──────────────────────────────────────────────

export async function fetchUserStreaks(userId) {
  const { data, error } = await supabase.rpc('get_user_streaks', { p_user_id: userId })
  if (error) throw error
  return data || { all_time_best: 0, per_bracket: [] }
}

export async function fetchUserBadges(userId) {
  const { data, error } = await supabase.rpc('get_user_badges', { p_user_id: userId })
  if (error) throw error
  return data || []
}
